<template>
  <div class="level" :id="`level-${this.level}`">
    <div class="post">
      <a href="/" target="_blank">new tab</a>
      <div class="button-grid" v-if="audioContextStarted">
        <button
          v-for="(node, index) in filterNodes"
          :key="index"
          class="grid-button"
          @click="togglePlay(index)"
          :style="{
            backgroundImage: `url('${gif}')`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            opacity: !isPlaying[index]
              ? convertFrequencyToAlpha(node.frequency.value)
              : 1,
          }"
        ></button>
      </div>
      <div class="controls">
        <div class="control">
          <input id="gain" type="range" min="0" max="1" step="0.01" value="1" />
          <select name="randGainIntervals">
            <option value="100">100</option>
            <option value="250">250</option>
            <option value="500">500</option>
            <option value="1000" selected>1000</option>
            <option value="2000">2000</option>
            <option value="5000">5000</option>
          </select>
          <button id="randGain">⚫</button>
        </div>
      </div>
      <div class="control">
        <input id="pan" type="range" min="-1" max="1" step="0.01" value="0" />
        <select name="randPanIntervals">
          <option value="100">100</option>
          <option value="250">250</option>
          <option value="500">500</option>
          <option value="1000" selected>1000</option>
          <option value="2000">2000</option>
          <option value="5000">5000</option>
        </select>
        <button id="randPan">⚫</button>
      </div>
      <div class="control">
        <input id="delay" type="range" min="0" max="1" step="0.01" value="0" />
        <select name="randDelayIntervals">
          <option value="100">100</option>
          <option value="250">250</option>
          <option value="500">500</option>
          <option value="1000" selected>1000</option>
          <option value="2000">2000</option>
          <option value="5000">5000</option>
        </select>
        <button id="randDelay">⚫</button>
      </div>
      <div class="control">
        <input id="q" type="range" min="10" max="1000" step="1" value="1000" />
        <select name="randQIntervals">
          <option value="100">100</option>
          <option value="250">250</option>
          <option value="500">500</option>
          <option value="1000" selected>1000</option>
          <option value="2000">2000</option>
          <option value="5000">5000</option>
        </select>
        <button id="randQ">⚫</button>
      </div>
    </div>
  </div>
</template>

<script>
import { createWhiteNoise } from "../utils/createWhiteNoise";
import gif from "../assets/the_gif.gif";

export default {
  props: {
    audioContext: {
      type: Object,
      required: true,
    },
    gridSize: {
      type: Number,
      required: true,
    },
    level: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      audioContextStarted: false,
      whiteNoiseBuffers: [],
      gainNodes: [],
      filterNodes: [],
      panners: [],
      delayNodes: [],
      freqMin: 100,
      freqMax: 3000,
      isPlaying: [],
      gridButtons: [],
      gif,
      gainInterval: null,
      panInterval: null,
      delayInterval: null,
      qInterval: null,
    };
  },
  mounted() {
    this.gridButtons = document.querySelectorAll(".grid-button");
    document.addEventListener("keydown", (e) => {
      const key = e.key;

      switch (key) {
        case "r":
          this.reverseSelection();
          break;
        case "a":
          this.startAll();
          break;
        case "s":
          this.stopAll();
          break;
        case "l":
          this.playLows();
          break;
        case "m":
          this.playMids();
          break;
        case "h":
          this.playHighs();
          break;
        case "w":
          this.playRandom();
          break;
        case "2":
          this.playEveryNth(key);
          break;
        case "3":
          this.playEveryNth(key);
          break;
        case "4":
          this.playEveryNth(key);
          break;
        case "5":
          this.playEveryNth(key);
          break;
      }

      document.addEventListener("keyup", (e) => {
        const key = e.key;

        switch (key) {
          case "l":
            this.stopLows();
            break;
          case "m":
            this.stopMids();
            break;
          case "h":
            this.stopHighs();
            break;
        }
      });
    });

    // audio controls
    const gain = document.getElementById("gain");
    gain.addEventListener("change", (e) => {
      let gainValue = parseFloat(e.target.value);
      this.changeGain(gainValue);
    });

    const pan = document.getElementById("pan");
    pan.addEventListener("change", (e) => {
      let panValue = parseFloat(e.target.value);
      this.pan(panValue);
    });

    const delay = document.getElementById("delay");
    delay.addEventListener("change", (e) => {
      let delayValue = parseFloat(e.target.value);
      this.changeDelay(delayValue);
    });

    const q = document.getElementById("q");
    q.addEventListener("change", (e) => {
      let qValue = parseFloat(e.target.value);
      this.changeQ(qValue);
    });

    document
      .getElementById("randGain")
      .addEventListener("click", () => this.toggleRandomization("Gain"));
    document
      .getElementById("randPan")
      .addEventListener("click", () => this.toggleRandomization("Pan"));
    document
      .getElementById("randDelay")
      .addEventListener("click", () => this.toggleRandomization("Delay"));
    document
      .getElementById("randQ")
      .addEventListener("click", () => this.toggleRandomization("Q"));
  },
  methods: {
    initAudioContext() {
      for (let i = 0; i < this.gridSize; i++) {
        this.whiteNoiseBuffers[i] = this.audioContext.createBufferSource();
      }

      this.initWhiteNoise();
      this.initGain();
      this.initFrequency();
      this.initDelay();
      this.initPanners();
      this.connect();

      this.isPlaying = Array(this.gridSize).fill(false);

      this.audioContextStarted = true;
    },
    initWhiteNoise() {
      for (let i = 0; i < this.whiteNoiseBuffers.length; i++) {
        this.whiteNoiseBuffers[i].buffer = createWhiteNoise(this.audioContext);
      }
    },
    initGain() {
      for (let i = 0; i < this.whiteNoiseBuffers.length; i++) {
        this.gainNodes[i] = this.audioContext.createGain();
        this.gainNodes[i].gain.setValueAtTime(0, this.audioContext.currentTime);
      }
    },
    initFrequency() {
      for (let i = 0; i < this.whiteNoiseBuffers.length; i++) {
        this.filterNodes[i] = this.audioContext.createBiquadFilter();
        this.filterNodes[i].type = "bandpass";
        this.filterNodes[i].frequency.value =
          Math.random() * (this.freqMax - this.freqMin + 1) + this.freqMin;
        this.filterNodes[i].Q.value = 1000;
      }
    },
    initDelay() {
      for (let i = 0; i < this.whiteNoiseBuffers.length; i++) {
        this.delayNodes[i] = this.audioContext.createDelay();
        this.delayNodes[i].delayTime.value = 0;
      }
    },
    initPanners() {
      for (let i = 0; i < this.whiteNoiseBuffers.length; i++) {
        this.panners[i] = this.audioContext.createStereoPanner();
        this.panners[i].pan.value = 0;
      }
    },
    connect() {
      for (let i = 0; i < this.whiteNoiseBuffers.length; i++) {
        this.whiteNoiseBuffers[i].connect(this.filterNodes[i]);
        this.filterNodes[i].connect(this.delayNodes[i]);
        this.delayNodes[i].connect(this.panners[i]);
        this.panners[i].connect(this.gainNodes[i]);
        this.gainNodes[i].connect(this.audioContext.destination);

        this.whiteNoiseBuffers[i].loop = true;
        this.whiteNoiseBuffers[i].start();
      }
    },
    togglePlay(i) {
      if (this.isPlaying[i]) {
        this.gainNodes[i].gain.setValueAtTime(
          this.gainNodes[i].gain.value,
          this.audioContext.currentTime
        );
        this.gainNodes[i].gain.linearRampToValueAtTime(
          0,
          this.audioContext.currentTime + 0.05
        );
        this.isPlaying[i] = false;
      } else {
        this.gainNodes[i].gain.setValueAtTime(
          this.gainNodes[i].gain.value,
          this.audioContext.currentTime
        );
        this.gainNodes[i].gain.linearRampToValueAtTime(
          1,
          this.audioContext.currentTime + 0.05
        );
        this.isPlaying[i] = true;
      }
    },
    convertFrequencyToAlpha(frequency) {
      return (frequency - this.freqMin) / (this.freqMax - this.freqMin) - 0.2;
    },
    handleGridSizeChange() {
      // turn off any currently playing nodes
      for (let i = 0; i < this.gainNodes.length; i++) {
        this.gainNodes[i].gain.setValueAtTime(
          this.gainNodes[i].gain.value,
          this.audioContext.currentTime
        );
        this.gainNodes[i].gain.linearRampToValueAtTime(
          0,
          this.audioContext.currentTime + 0.05
        );
      }
      this.audioContextStarted = false;
      this.whiteNoiseBuffers = [];
      this.gainNodes = [];
      this.filterNodes = [];
      this.audioContextStarted = false;
      this.initAudioContext();

      // after the DOM has finished updating
      this.$nextTick(() => {
        this.gridButtons = document.querySelectorAll(".grid-button");
      });
    },
    reverseSelection() {
      this.gridButtons.forEach((button) => {
        button.click();
      });
    },
    startAll() {
      for (let i = 0; i < this.isPlaying.length; i++) {
        if (!this.isPlaying[i]) {
          this.togglePlay(i);
        }
      }
    },
    stopAll() {
      for (let i = 0; i < this.isPlaying.length; i++) {
        if (this.isPlaying[i]) {
          this.togglePlay(i);
        }
      }
    },
    playLows() {
      for (let i = 0; i < this.filterNodes.length; i++) {
        const frequency = this.filterNodes[i].frequency.value;
        if (frequency < this.freqMin + (this.freqMax - this.freqMin) / 3) {
          if (!this.isPlaying[i]) {
            this.togglePlay(i);
          }
        }
      }
    },
    stopLows() {
      for (let i = 0; i < this.filterNodes.length; i++) {
        const frequency = this.filterNodes[i].frequency.value;
        if (frequency < this.freqMin + (this.freqMax - this.freqMin) / 3) {
          if (this.isPlaying[i]) {
            this.togglePlay(i);
          }
        }
      }
    },
    playMids() {
      for (let i = 0; i < this.filterNodes.length; i++) {
        const frequency = this.filterNodes[i].frequency.value;
        if (
          frequency >= this.freqMin + (this.freqMax - this.freqMin) / 3 &&
          frequency <= this.freqMin + (2 * (this.freqMax - this.freqMin)) / 3
        ) {
          if (!this.isPlaying[i]) {
            this.togglePlay(i);
          }
        }
      }
    },
    stopMids() {
      for (let i = 0; i < this.filterNodes.length; i++) {
        const frequency = this.filterNodes[i].frequency.value;
        if (
          frequency >= this.freqMin + (this.freqMax - this.freqMin) / 3 &&
          frequency <= this.freqMin + (2 * (this.freqMax - this.freqMin)) / 3
        ) {
          if (this.isPlaying[i]) {
            this.togglePlay(i);
          }
        }
      }
    },
    playHighs() {
      for (let i = 0; i < this.filterNodes.length; i++) {
        const frequency = this.filterNodes[i].frequency.value;
        if (
          frequency >
          this.freqMin + (2 * (this.freqMax - this.freqMin)) / 3
        ) {
          if (!this.isPlaying[i]) {
            this.togglePlay(i);
          }
        }
      }
    },
    stopHighs() {
      for (let i = 0; i < this.filterNodes.length; i++) {
        const frequency = this.filterNodes[i].frequency.value;
        if (
          frequency >
          this.freqMin + (2 * (this.freqMax - this.freqMin)) / 3
        ) {
          if (this.isPlaying[i]) {
            this.togglePlay(i);
          }
        }
      }
    },
    playRandom() {
      let randomIndex = Math.floor(Math.random() * this.filterNodes.length);
      this.togglePlay(randomIndex);
    },
    playEveryNth(key) {
      for (let i = 0; i < this.isPlaying.length; i++) {
        if (!this.isPlaying[i]) {
          if (i % key === 0) {
            this.togglePlay(i);
          }
        }
      }
    },
    changeGain(gainValue) {
      for (let i = 0; i < this.gainNodes.length; i++) {
        if (this.isPlaying[i]) {
          this.gainNodes[i].gain.setValueAtTime(
            this.gainNodes[i].gain.value,
            this.audioContext.currentTime
          );
          this.gainNodes[i].gain.linearRampToValueAtTime(
            gainValue,
            this.audioContext.currentTime + 0.05
          );
        }
      }
    },
    pan(panValue) {
      for (let i = 0; i < this.panners.length; i++) {
        this.panners[i].pan.linearRampToValueAtTime(
          panValue,
          this.audioContext.currentTime + 0.05
        );
      }
    },
    changeDelay(delayValue) {
      for (let i = 0; i < this.delayNodes.length; i++) {
        this.delayNodes[i].delayTime.setValueAtTime(
          this.delayNodes[i].delayTime.value,
          this.audioContext.currentTime
        );
        this.delayNodes[i].delayTime.linearRampToValueAtTime(
          delayValue,
          this.audioContext.currentTime + 0.05
        );
      }
    },
    changeQ(qValue) {
      for (let i = 0; i < this.filterNodes.length; i++) {
        this.filterNodes[i].Q.setValueAtTime(
          this.filterNodes[i].Q.value,
          this.audioContext.currentTime
        );
        this.filterNodes[i].Q.linearRampToValueAtTime(
          qValue,
          this.audioContext.currentTime + 0.05
        );
      }
    },
    toggleRandomization(param) {
      //const button = document.getElementById(`rand${param}`);
      const intervalSelector = document.querySelector(
        `[name='rand${param}Intervals']`
      );
      const rangeInput = document.getElementById(param.toLowerCase());

      if (this[`${param.toLowerCase()}Interval`]) {
        clearInterval(this[`${param.toLowerCase()}Interval`]);
        this[`${param.toLowerCase()}Interval`] = null;
        return;
      }

      this[`${param.toLowerCase()}Interval`] = setInterval(() => {
        let randomValue;
        switch (param) {
          case "Gain":
            randomValue = Math.random();
            this.changeGain(randomValue);
            break;
          case "Pan":
            randomValue = Math.random() * 2 - 1;
            this.pan(randomValue);
            break;
          case "Delay":
            randomValue = Math.random();
            this.changeDelay(randomValue);
            break;
          case "Q":
            randomValue = Math.floor(Math.random() * (1000 - 10 + 1)) + 10;
            this.changeQ(randomValue);
            break;
        }
        rangeInput.value = randomValue;
      }, parseInt(intervalSelector.value));
    },
  },
  watch: {
    gridSize: {
      handler(newVal) {
        this.handleGridSizeChange(newVal);
      },
      immediate: true,
    },
  },
};
</script>

<style>
.post {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 12px;
}

#level-2 > .post > .button-grid {
  grid-template-columns: repeat(2, 1fr);
}

#level-3 > .post > .button-grid {
  grid-template-columns: repeat(3, 1fr);
}

#level-4 > .post > .button-grid {
  grid-template-columns: repeat(4, 1fr);
}

#level-5 > .post > .button-grid {
  grid-template-columns: repeat(5, 1fr);
}

#level-6 > .post > .button-grid {
  grid-template-columns: repeat(6, 1fr);
}

#level-7 > .post > .button-grid {
  grid-template-columns: repeat(7, 1fr);
}

#level-8 > .post > .button-grid {
  grid-template-columns: repeat(8, 1fr);
}

#level-9 > .post > .button-grid {
  grid-template-columns: repeat(9, 1fr);
}

/* #level-10 > .post > .button-grid {
  grid-template-columns: repeat(10, 1fr);
}

#level-11 > .post > .button-grid {
  grid-template-columns: repeat(11, 1fr);
}

#level-12 > .post > .button-grid {
  grid-template-columns: repeat(12, 1fr);
} */

.button-grid {
  display: grid;
  width: 100%;
  grid-template-columns: repeat(1, 1fr);
  aspect-ratio: 1 / 1;
  max-width: 40vw;
  gap: 2px;
}

.grid-button {
  width: 100%;
  height: 100%;
  border: none;
  aspect-ratio: 1 / 1;
}

/* range input style */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  width: 40vw;
  height: 6px;
  background: white;
  border-radius: 3px;
  outline: none;
  opacity: 0.8;
  transition: opacity 0.2s;
  margin: 1em 0;
}

input[type="range"]:focus {
  border: 2px solid rgb(224, 155, 26);
  background: black;
}

/* WebKit (Chrome, Safari) */
input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  cursor: pointer;
}

/* Mozilla Firefox */
input[type="range"]::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  cursor: pointer;
}
</style>
